import { isLeaveRecordVisible } from '../utils/leaveVisibility';
import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { CONFIGURATION_RECORD_INACTIVE } from '../constants/configuration';
import { nextDisplayOrder } from '../utils/displayOrder';
import type {
  CreateLeaveConfigurationInput,
  LeaveConfiguration,
  LeaveConfigurationDurationRule,
  UpdateLeaveConfigurationInput,
} from '../types/leaveConfiguration';

type Envelope<T> = { success: boolean; message: string; data: T };
type Page = {
  items: LeaveConfiguration[];
  pagination: { page: number; pageSize: number; totalRecords: number; totalPages: number };
};

function days(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function optionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeDurationRule(
  rule: LeaveConfigurationDurationRule,
): LeaveConfigurationDurationRule {
  return {
    ...rule,
    durationCode: String(rule.durationCode || '').toUpperCase(),
    durationDays: optionalNumber(rule.durationDays),
    isHalfDay: Boolean(rule.isHalfDay),
    requiresExplicitRule: Boolean(rule.requiresExplicitRule),
    hasRule: Boolean(rule.hasRule),
    isAllowed: Boolean(rule.isAllowed),
    minimumAdvanceDays: days(rule.minimumAdvanceDays),
    maximumRequestsPerMonth: optionalNumber(rule.maximumRequestsPerMonth),
  };
}

function normalize(item: LeaveConfiguration): LeaveConfiguration {
  return {
    ...item,
    leaveTypeId: Number(item.leaveTypeId),
    leavePolicyId: Number(item.leavePolicyId),
    annualEntitlement: days(item.annualEntitlement),
    monthlyAccrual: days(item.monthlyAccrual),
    currentYearEmployeeCount: Number(item.currentYearEmployeeCount || 0),
    rowVersion: Number(item.rowVersion),
    displayOrder: Number(item.displayOrder || 0),
    allowFullDay: Boolean(item.allowFullDay),
    allowHalfDay: Boolean(item.allowHalfDay),
    isPaid: Boolean(item.isPaid),
    requiresBalance: Boolean(item.requiresBalance),
    receivesCompensatoryCredit: Boolean(item.receivesCompensatoryCredit),
    isDefault: Boolean(item.isDefault),
    isActive: Boolean(item.isActive),
    accrualBasis: item.accrualBasis ? String(item.accrualBasis).toUpperCase() : null,
    carryForwardAllowed:
      item.carryForwardAllowed == null ? null : Boolean(Number(item.carryForwardAllowed)),
    maxCarryForwardDays: optionalNumber(item.maxCarryForwardDays),
    durationRules: Array.isArray(item.durationRules)
      ? item.durationRules.map(normalizeDurationRule)
      : [],
  };
}

export async function getLeaveConfigurations(
  search = '',
  filters?: { isActive?: boolean },
): Promise<Page> {
  const response = await apiClient.get<Envelope<Page>>(
    API_ENDPOINTS.LEAVE_CONFIGURATIONS.LIST,
    {
      params: {
        search: search.trim() || undefined,
        page: 1,
        pageSize: 100,
        ...(filters?.isActive === true || filters?.isActive === false
          ? { isActive: filters.isActive }
          : {}),
      },
    },
  );
  return { ...response.data.data, items: response.data.data.items.filter(isLeaveRecordVisible).map(normalize) };
}

function matchesCreatedConfiguration(
  item: LeaveConfiguration,
  input: CreateLeaveConfigurationInput,
): boolean {
  const typeCode = String(input.leaveTypeCode || '').trim().toUpperCase();
  const policyCode = String(input.policyCode || '').trim().toUpperCase();
  return (
    String(item.leaveTypeCode || '').toUpperCase() === typeCode
    || String(item.policyCode || '').toUpperCase() === policyCode
  );
}

/** Recovers a committed create when the API body omitted the new policy row. */
async function recoverCreatedConfiguration(
  input: CreateLeaveConfigurationInput,
): Promise<LeaveConfiguration | null> {
  const listed = await getLeaveConfigurations('', { isActive: true });
  const match = listed.items.find(item => matchesCreatedConfiguration(item, input));
  return match ? normalize(match) : null;
}

function isRecoverableCreateFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code?: unknown }).code || '')
      : '';
  const responseData =
    error && typeof error === 'object' && 'response' in error
      ? (error as { response?: { data?: { code?: unknown; message?: unknown } } }).response?.data
      : undefined;
  const responseCode = String(responseData?.code || '');
  const responseMessage = String(responseData?.message || '');
  return (
    code === 'LEAVE_CONFIGURATION_LOAD_FAILED'
    || responseCode === 'LEAVE_CONFIGURATION_LOAD_FAILED'
    || /incomplete response|could not be loaded/i.test(message)
    || /could not be loaded/i.test(responseMessage)
  );
}

export async function createLeaveConfiguration(
  input: CreateLeaveConfigurationInput,
): Promise<LeaveConfiguration> {
  const existing = await getLeaveConfigurations();
  const displayOrder = nextDisplayOrder(existing.items, existing.pagination.totalRecords);
  try {
    const response = await apiClient.post<Envelope<LeaveConfiguration>>(
      API_ENDPOINTS.LEAVE_CONFIGURATIONS.CREATE,
      { ...input, displayOrder },
    );
    const created = response.data?.data;
    if (created && created.leavePolicyId != null) {
      return normalize(created);
    }
    const recovered = await recoverCreatedConfiguration(input);
    if (recovered) return recovered;
    throw new Error('Leave configuration was created but the server returned an incomplete response.');
  } catch (caught) {
    if (isRecoverableCreateFailure(caught)) {
      const recovered = await recoverCreatedConfiguration(input);
      if (recovered) return recovered;
    }
    throw caught;
  }
}

export async function updateLeaveConfiguration(
  leavePolicyId: number,
  input: UpdateLeaveConfigurationInput,
): Promise<LeaveConfiguration> {
  const response = await apiClient.put<Envelope<LeaveConfiguration>>(
    API_ENDPOINTS.LEAVE_CONFIGURATIONS.UPDATE(leavePolicyId),
    input,
  );
  return normalize(response.data.data);
}

export async function deactivateLeaveConfiguration(
  item: LeaveConfiguration,
): Promise<LeaveConfiguration> {
  // Soft-delete only sends isActive=false so validation cannot reject
  // duration-rule-only leave types (both allowFullDay/allowHalfDay false).
  const response = await apiClient.put<Envelope<LeaveConfiguration>>(
    API_ENDPOINTS.LEAVE_CONFIGURATIONS.UPDATE(item.leavePolicyId),
    { isActive: CONFIGURATION_RECORD_INACTIVE },
  );
  const deleted = response.data?.data;
  if (!deleted) {
    throw new Error('Leave configuration deletion was not confirmed by the server.');
  }
  return normalize({
    ...item,
    ...deleted,
    leavePolicyId: Number(deleted.leavePolicyId ?? item.leavePolicyId),
    leaveTypeId: Number(deleted.leaveTypeId ?? item.leaveTypeId),
    isActive: false,
  });
}

export async function generateLeaveBalances(
  leavePolicyId: number,
  leaveYear: number,
): Promise<{ createdCount: number; leaveYear: number }> {
  const response = await apiClient.post<
    Envelope<{ createdCount: number; leaveYear: number }>
  >(API_ENDPOINTS.LEAVE_CONFIGURATIONS.GENERATE_BALANCES(leavePolicyId), {
    leaveYear,
  });
  return {
    createdCount: Number(response.data.data.createdCount || 0),
    leaveYear: Number(response.data.data.leaveYear),
  };
}
